import React from "react";
import ReactApexChart from "react-apexcharts";

const Line = ({
    series = [],
    leftText,
    bottomText,
    maxXaxis,
    dataColors,
    showAvgCheck = false,
    currency = '$'
}) => {

    let columnData = [];
    let lineData = [];
    let avgCheckData = [];
    let dateData = [];

    for (let i = 0; i < series.length; i++) {

        // Revenue
        columnData.push(
            Number(series[i]?.revenue || 0)
        );

        // Item Sold
        lineData.push(
            Number(series[i]?.chk_count || 0)
        );

        // Avg Check
        avgCheckData.push(
            Number(series[i]?.avg_check || 0)
        );

        // Date
        dateData.push(
            series[i]?.row_dt || ''
        );
    }

   
     //SERIES
   

    const seriesvalues = [
        {
            name: "Revenue",
            type: "column",
            data: columnData,
        },
        {
            name: "Item Sold",
            type: "column",
            data: lineData,
        },
    ];

     //Always show Avg Check when showAvgCheck is true.

    if (showAvgCheck) {
        seriesvalues.push({
            name: "Avg Check",
            type: "line",
            data: avgCheckData,
        });
    }

    
    // COLORS
    const colors = showAvgCheck
        ? [
            '#5569A6', // Revenue
            '#F26B5B', // Item Sold
            '#F7B84B', // Avg Check
        ]
        : [
            '#5569A6', // Revenue
            '#F26B5B', // Item Sold
        ];

     //FIND MAX VALUES
     
    // Calculate proper max values with better scaling
    const allRevenueValues = columnData.length
        ? columnData
        : [0];

    const allItemSoldValues = lineData.length
        ? lineData
        : [0];

    const allAvgCheckValues = avgCheckData.length
        ? avgCheckData
        : [0];

    const revenueMax = Math.max(...allRevenueValues, 0);
    const itemSoldMax = Math.max(...allItemSoldValues, 0);
    const avgCheckMax = Math.max(...allAvgCheckValues, 0);

    // Better max calculation for revenue axis
    let revenueAxisMax;
    if (revenueMax === 0) {
        revenueAxisMax = 1;
    } else if (revenueMax <= 1) {
        revenueAxisMax = 1;
    } else if (revenueMax <= 5) {
        revenueAxisMax = 5;
    } else if (revenueMax <= 10) {
        revenueAxisMax = 10;
    } else {
        revenueAxisMax = Math.ceil(revenueMax * 1.1);
    }

    // Better max calculation for right axis (Item Sold + Avg Check)
    const rightAxisMax = Math.max(
        itemSoldMax,
        avgCheckMax,
        0
    );

    let safeRightAxisMax;
    if (rightAxisMax === 0) {
        safeRightAxisMax = 1;
    } else if (rightAxisMax <= 1) {
        safeRightAxisMax = 1;
    } else if (rightAxisMax <= 5) {
        safeRightAxisMax = 5;
    } else if (rightAxisMax <= 10) {
        safeRightAxisMax = 10;
    } else if (rightAxisMax <= 50) {
        safeRightAxisMax = Math.ceil(rightAxisMax / 10) * 10;
    } else {
        safeRightAxisMax = Math.ceil(rightAxisMax * 1.1);
    }

    // Determine tick amount based on max value
    const getTickAmount = (maxVal) => {
        if (maxVal <= 5) return 5;
        if (maxVal <= 10) return 5;
        if (maxVal <= 50) return 10;
        if (maxVal <= 100) return 10;
        if (maxVal <= 500) return 10;
        return 10;
    };

    const revenueTickAmount = getTickAmount(revenueAxisMax);
    const rightTickAmount = getTickAmount(safeRightAxisMax);

    
     // OPTIONS
    
    const options = {

        chart: {
            toolbar: {
                show: true,
            },
        },

        stroke: {
            width: showAvgCheck
                ? [0, 0, 3]
                : [0, 0],
        },

         // DATA LABELS - ONLY SHOW FOR AVG CHECK
        
        dataLabels: {
            enabled: true,

            // Only show data labels for Avg Check (series index 2)
            enabledOnSeries: showAvgCheck
                ? [2]  // Only Avg Check
                : [],  // No data labels when avg check is not shown

            // Position the label at the top center of each bar
            position: 'top',

            formatter: function (
                value,
                {
                    seriesIndex
                }
            ) {

                // Avg Check - show with 1 decimal
                if (seriesIndex === 2) {
                    return Number(value).toFixed(1);
                }

                return value;
            },

            offsetY: -5,

            style: {
                fontSize: '10px',
                fontWeight: 400,
            },
        },
        
        // X AXIS   

        labels: dateData,

        xaxis: {
            labels: {
                rotate: -45,
                rotateAlways: false,
                hideOverlappingLabels: true,
                showDuplicates: false,
                trim: true,
                minHeight: 30,
                maxHeight: 60,

                style: {
                    fontSize: '10px',
                },
            },

            categories: dateData,

            tickAmount: Math.min(
                Math.max(dateData.length, 1),
                20
            ),

            min: dateData.length > 0 ? undefined : 0,
            max: dateData.length > 0
                ? undefined
                : 1,
        },

        // Y AXIS
     

        yaxis: [
            {
                
                 // Revenue axis
                min: 0,

                max: revenueAxisMax,

                forceNiceScale: true,

                tickAmount: revenueTickAmount,

                title: {
                    text: leftText || "Revenue",

                    style: {
                        fontWeight: 600,
                    },
                },

                labels: {
                    formatter: function (value) {
                        // Show as whole numbers
                        return Math.round(value);
                    },
                },
            },

            {

                opposite: true,

                min: 0,

                max: safeRightAxisMax,

                forceNiceScale: true,

                tickAmount: rightTickAmount,

                title: {
                    text: "Avg Check / Item Sold",

                    style: {
                        fontWeight: 600,
                    },
                },

                labels: {
                    // Show as whole numbers (integers) without decimals
                    formatter: function (value) {
                        // Round to nearest integer
                        return Math.round(value);
                    },
                },
            },
        ],

        colors: colors,

        legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            floating: false,
            offsetY: 0,
            offsetX: 0,

            fontSize: '12px',
            fontWeight: 500,

            markers: {
                width: 12,
                height: 12,
                strokeWidth: 0,
                radius: 2,
            },

            itemMargin: {
                horizontal: 10,
                vertical: 5,
            },
        },

        // TOOLTIP - SHOW ALL THREE DATA
        
        tooltip: {
            shared: true,
            intersect: false,

            y: {
                formatter: function (
                    value,
                    {
                        seriesIndex
                    }
                ) {

                    // Avg Check - show with 1 decimal in tooltip
                    if (showAvgCheck && seriesIndex === 2) {
                        return Number(value).toFixed(1);
                    }

                    // Revenue / Item Sold - show as whole numbers
                    return Math.round(value);
                },
            },
        }, 

        // GRID

        grid: {
            borderColor: '#e0e0e0',

            row: {
                colors: [
                    'transparent',
                    'transparent'
                ],
                opacity: 0,
            },
        },

        // PLOT OPTIONS

        plotOptions: {
            bar: {
                columnWidth: '60%',
                borderRadius: 2,
            },
        },

        noData: {
            text: '',
        },
    };

    return (
        <React.Fragment>
            <ReactApexChart
                options={options}
                series={seriesvalues}
                type="line"
                height={350}
            />
        </React.Fragment>
    );
};

export {
    Line
};